import { supabase } from "../lib/supabase";
import { Booking } from "../types/booking";
import moment from "moment";

/**
 * Fetch booking data for export with optional filters
 */
export const fetchBookingsForExport = async (
  filters: {
    dateRange?: [string, string];
    status?: string;
    transportTypeId?: string | number;
    agentId?: number;
    isConsolidation?: boolean;
  } = {}
) => {
  try {
    const {
      dateRange,
      status,
      transportTypeId,
      agentId,
      isConsolidation,
    } = filters;

    // Log the filters being applied
    console.log("Booking export filters:", {
      dateRange,
      status,
      transportTypeId,
      agentId,
      isConsolidation,
    });

    // Prepare the query
    let query = supabase
      .from("bookings")
      .select(
        `
        *,
        transport_type_info:booking_transport_types(*),
        booking_type_info:bookings_type(*),
        company_info:company(*),
        supplier_info:booking_supplier(*),
        origin_port_info:booking_origin_port(*),
        destination_port_info:booking_destination_port(*),
        booking_incoterm_info:bookings_incoterm(*),
        agent_info:booking_agent(*),
        product_info:booking_product(*),
        container_info:containers(*),
        booking_delivery_info:booking_delivery(*)
      `
      )
      .neq("company_id", 3); // Exclude bookings from company_id = 3

    // Add status filter if provided
    if (status) {
      query = query.eq("status", status);
    }

    // Add date range filter if provided
    if (dateRange && dateRange.length === 2) {
      // Log date range for debugging
      console.log(`Applying date range filter: ${dateRange[0]} to ${dateRange[1]}`);
      
      query = query
        .gte("created_at", dateRange[0])
        .lte("created_at", dateRange[1]);
    }

    // Add transport type filter if provided
    if (transportTypeId) {
      query = query.eq("transport_type", transportTypeId);
    }

    // Add agent filter if provided
    if (agentId) {
      query = query.eq("agent_id", agentId);
    }

    // Add consolidation filter if provided
    if (isConsolidation !== undefined) {
      if (isConsolidation) {
        // If consolidated, container_id should not be null
        query = query.not("container_id", "is", null);
      } else {
        // If not consolidated, container_id should be null
        query = query.is("container_id", null);
      }
    }

    // Default sorting by creation date (newest first)
    query = query.order("created_at", { ascending: false });

    // First, get the total count with a separate count query
    let countQuery = supabase
      .from("bookings")
      .select("*", { count: "exact", head: true })
      .neq("company_id", 3); // Exclude bookings from company_id = 3

    // Apply the same filters to the count query
    if (status) {
      countQuery = countQuery.eq("status", status);
    }
    if (dateRange && dateRange.length === 2) {
      countQuery = countQuery
        .gte("created_at", dateRange[0])
        .lte("created_at", dateRange[1]);
    }
    if (transportTypeId) {
      countQuery = countQuery.eq("transport_type", transportTypeId);
    }
    if (agentId) {
      countQuery = countQuery.eq("agent_id", agentId);
    }
    if (isConsolidation !== undefined) {
      if (isConsolidation) {
        countQuery = countQuery.not("container_id", "is", null);
      } else {
        countQuery = countQuery.is("container_id", null);
      }
    }

    const { count, error: countError } = await countQuery;
    
    if (countError) throw countError;

    const totalCount = count || 0;
    console.log(`Total bookings matching filters: ${totalCount}`);

    // Fetch all bookings in batches (Supabase limit is 1000 per request)
    const batchSize = 1000;
    const allBookings: Booking[] = [];
    let offset = 0;

    while (offset < totalCount) {
      let batchQuery = supabase
        .from("bookings")
        .select(
          `
          *,
          transport_type_info:booking_transport_types(*),
          booking_type_info:bookings_type(*),
          company_info:company(*),
          supplier_info:booking_supplier(*),
          origin_port_info:booking_origin_port(*),
          destination_port_info:booking_destination_port(*),
          booking_incoterm_info:bookings_incoterm(*),
          agent_info:booking_agent(*),
          product_info:booking_product(*),
          container_info:containers(*),
          booking_delivery_info:booking_delivery(*)
        `
        )
        .neq("company_id", 3) // Exclude bookings from company_id = 3
        .order("created_at", { ascending: false })
        .range(offset, offset + batchSize - 1);

      // Reapply all filters for this batch
      if (status) {
        batchQuery = batchQuery.eq("status", status);
      }
      if (dateRange && dateRange.length === 2) {
        batchQuery = batchQuery
          .gte("created_at", dateRange[0])
          .lte("created_at", dateRange[1]);
      }
      if (transportTypeId) {
        batchQuery = batchQuery.eq("transport_type", transportTypeId);
      }
      if (agentId) {
        batchQuery = batchQuery.eq("agent_id", agentId);
      }
      if (isConsolidation !== undefined) {
        if (isConsolidation) {
          batchQuery = batchQuery.not("container_id", "is", null);
        } else {
          batchQuery = batchQuery.is("container_id", null);
        }
      }

      const { data: batchData, error: batchError } = await batchQuery;

      if (batchError) throw batchError;

      if (batchData) {
        allBookings.push(...(batchData as Booking[]));
        console.log(`Fetched batch: ${batchData.length} bookings (total so far: ${allBookings.length})`);
      }

      // If we got fewer results than batch size, we've reached the end
      if (!batchData || batchData.length < batchSize) {
        break;
      }

      offset += batchSize;
    }

    // Log the number of results found
    console.log(`Booking query returned ${allBookings.length} results (out of ${totalCount} total)`);

    return { 
      data: allBookings, 
      error: null 
    };
  } catch (error) {
    console.error("Error fetching bookings for export:", error);
    return { 
      data: [] as Booking[], 
      error 
    };
  }
};

/**
 * Generate CSV/Excel compatible data from booking data
 */
export const prepareBookingExportData = (bookings: Booking[]) => {
  return bookings.map(booking => {
    // Handle company info
    let companyName = "Unknown";
    if (booking.company_info) {
      if (Array.isArray(booking.company_info) && booking.company_info.length > 0) {
        companyName = booking.company_info[0].name || "Unknown";
      } else if (!Array.isArray(booking.company_info)) {
        companyName = booking.company_info.name || "Unknown";
      }
    }

    // Handle transport type info
    let transportTypeName = "Unknown";
    if (booking.transport_type_info) {
      if (Array.isArray(booking.transport_type_info) && booking.transport_type_info.length > 0) {
        transportTypeName = booking.transport_type_info[0].name || "Unknown";
      } else if (!Array.isArray(booking.transport_type_info)) {
        transportTypeName = booking.transport_type_info.name || "Unknown";
      }
    }

    // Handle booking type info
    let bookingTypeName = "Unknown";
    if (booking.booking_type_info) {
      if (Array.isArray(booking.booking_type_info) && booking.booking_type_info.length > 0) {
        bookingTypeName = booking.booking_type_info[0].name || "Unknown";
      } else if (!Array.isArray(booking.booking_type_info)) {
        bookingTypeName = booking.booking_type_info.name || "Unknown";
      }
    }

    // Handle agent info
    let agentName = null;
    if (booking.agent_info) {
      if (Array.isArray(booking.agent_info) && booking.agent_info.length > 0) {
        agentName = booking.agent_info[0].name;
      } else if (!Array.isArray(booking.agent_info)) {
        agentName = booking.agent_info.name;
      }
    }

    // Handle container info
    let containerReference = null;
    if (booking.container_info) {
      if (Array.isArray(booking.container_info) && booking.container_info.length > 0) {
        containerReference = booking.container_info[0].reference;
      } else if (!Array.isArray(booking.container_info)) {
        containerReference = booking.container_info.reference;
      }
    }

    // Calculate products stats
    const products = Array.isArray(booking.product_info) ? booking.product_info : [];
    const productsCount = products.length;
    
    // Calculate totals (sum of all products)
    const totalCbm = products.reduce((sum, product) => sum + (product.cbm || 0), 0);
    const totalWeight = products.reduce((sum, product) => sum + (product.gross_weight || 0), 0);
    const totalPieces = products.reduce((sum, product) => sum + (product.pieces || 0), 0);
    const totalCartons = products.reduce((sum, product) => sum + (product.cartons || 0), 0);
    const totalValue = products.reduce((sum, product) => sum + (product.value || 0), 0);

    return {
      ID: booking.id,
      Reference: booking.reference || `EFF${booking.id}`,
      Status: booking.status,
      CreatedDate: moment(booking.created_at).format('YYYY-MM-DD'),
      Company: companyName,
      TransportType: transportTypeName,
      BookingType: bookingTypeName,
      Agent: agentName || 'None',
      ProductsCount: productsCount,
      TotalCBM: totalCbm.toFixed(3),
      TotalWeight: totalWeight.toFixed(2),
      TotalPieces: totalPieces,
      TotalCartons: totalCartons,
      TotalValue: totalValue.toFixed(2),
      ContainerReference: containerReference || 'N/A',
      IsConsolidated: containerReference ? 'Yes' : 'No',
    };
  });
};

/**
 * Get suggested filename for export
 */
export const getExportFilename = (prefix = "bookings-export") => {
  const dateStr = moment().format('YYYY-MM-DD');
  return `${prefix}-${dateStr}.csv`;
}; 